import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FeaturedIcon,
  BackrsForm,
  BackrsFormValues,
  TermsAndConditions,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useLazyFetchAutocompleteOptionsQuery,
  AutocompleteResult,
} from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { useOnboardingFormFields } from './hooks/useGetFormFields.hook';
import { OnboardingFlow, OnboardingQuestion } from './onboarding.types';
import { OnboardingSidebar } from './OnboardingSidebar';
import { getFieldToQuestionIndex } from './questions/questions.config';
import { Recommendations } from './Recommendations';
import { getRecommendations } from './utils/getRecommendations';

type OnboardingPageProps = Readonly<{
  currentFlow: OnboardingFlow;
  currentQuestion?: OnboardingQuestion;
  goToNextQuestion: (data: BackrsFormValues) => Promise<void>;
  questionKeys: string[];
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
  isShowingAccountCreationLoading: boolean;
  isViewingRecommendations: boolean;
}>;

export const OnboardingPage = ({
  currentQuestion,
  goToNextQuestion,
  questionKeys,
  currentQuestionIndex,
  currentFlow,
  setCurrentQuestionIndex,
  isShowingAccountCreationLoading,
  isViewingRecommendations,
}: OnboardingPageProps) => {
  const navigate = useNavigate();

  // Local state for autocomplete options; each option is keyed by the API endpoint
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<AutocompleteResult[]>([]);

  const [triggerFetchAutocomplete, { data: autocompleteData }] =
    useLazyFetchAutocompleteOptionsQuery();

  // Initialize a form instance with onSubmit validation mode
  const formMethods = useForm<BackrsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {},
  });

  const handleInputChange = (input: string, endpoint: string) => {
    triggerFetchAutocomplete({ endpoint, searchTerm: input });
  };

  // Update local autocomplete options when new API data arrives
  useEffect(() => {
    if (autocompleteData && Array.isArray(autocompleteData) && autocompleteData.length) {
      const newOptions = autocompleteData.map((data) => {
        const existingOption = autoCompleteOptions.find(
          (option) => option.endpoint === data.endpoint,
        );

        return {
          ...data,
          searchTerm: existingOption ? existingOption.searchTerm : '',
        };
      });

      if (JSON.stringify(newOptions) !== JSON.stringify(autoCompleteOptions)) {
        setAutoCompleteOptions(newOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteData]);

  // Use the custom hook to generate the form fields based on the current question
  const fields = useOnboardingFormFields({
    currentQuestion,
    questionKeys,
    currentQuestionIndex,
    autoCompleteOptions,
    setAutoCompleteOptions,
    handleInputChange,
  });

  /**
   * if there is an error returned from the API, we will navigate back to the form page that the field is in
   * Ex: if email is already in use and is on question 3/4, the API returns an error with the email field. We will then navigate back to question 3 and display the error with RHF
   */
  const handleErrorNavigation = (error: unknown) => {
    if (error && typeof error === 'object' && 'data' in error) {
      const typedError = error as { data: Record<string, string> };

      const fields = Object.keys(typedError.data);

      fields.forEach((field) => {
        formMethods.setError(field as keyof BackrsFormValues, {
          type: 'manual',
          message: typedError.data[field],
        });
      });

      const firstFieldWithError = fields[0];

      if (firstFieldWithError) {
        const targetIndex = getFieldToQuestionIndex(currentFlow)[firstFieldWithError];

        if (targetIndex !== undefined) {
          setCurrentQuestionIndex(targetIndex);
        }
      }
    }
  };

  const determineSidebarCard = (): 'data-locked' | 'team-will-contact' | undefined => {
    const indexOfCreateYourAccount = questionKeys.findIndex((key) => key === 'CREATE_ACCOUNT');

    if (indexOfCreateYourAccount === currentQuestionIndex) {
      return 'data-locked';
    }

    if (currentFlow === 'employer' && isViewingRecommendations) {
      return 'team-will-contact';
    }

    return undefined;
  };

  const handleClearEmptyFields = (data: BackrsFormValues) => {
    return Object.fromEntries(
      Object.entries(data).filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;

        return value !== '' && value !== null && value !== undefined;
      }),
    );
  };

  return (
    <Box sx={containerStyle}>
      <OnboardingSidebar
        currentQuestionIndex={
          isViewingRecommendations || isShowingAccountCreationLoading
            ? questionKeys.length
            : currentQuestionIndex
        }
        currentFlow={currentFlow}
        sidebarCard={determineSidebarCard()}
      />

      {isShowingAccountCreationLoading && (
        <Box sx={contentContainerStyle(false)}>
          <FeaturedIcon iconName="Compass01" size="xl" color="Colors/Foreground/fg-secondary" />
          <Typography variant="display-sm-semibold" sx={loadingTextStyle}>
            {appStrings.modules.onboarding.orchestratingAccountExistence}
          </Typography>

          <Box
            component="img"
            src="/images/orchestrating-account-existence.svg"
            sx={loadingImageStyle}
          />
        </Box>
      )}

      {isViewingRecommendations && (
        <Box sx={contentContainerStyle(false)}>
          <Recommendations
            onboardingType={currentFlow}
            recommendations={getRecommendations(formMethods.getValues(), currentFlow)}
          />
        </Box>
      )}

      {/* WE CANNOT CONDITIONALLY RENDER THE FORM WHEN LOADING, OTHERWISE WE BREAK RHF ERROR STATE */}
      {currentQuestion && !isViewingRecommendations && !isShowingAccountCreationLoading && (
        <Box sx={contentContainerStyle(isShowingAccountCreationLoading)}>
          <Box sx={questionContainerStyle}>
            <Box sx={questionHeaderContainerStyle}>
              <FeaturedIcon
                iconName={currentQuestion.featuredIcon}
                size="xl"
                color="Colors/Foreground/fg-secondary"
              />
              <Box sx={questionTitleContainerStyle}>
                <Typography variant="display-sm-semibold" component="div">
                  {currentQuestion.title}
                </Typography>
                <Typography variant="text-md-regular">{currentQuestion.subTitle}</Typography>
              </Box>
            </Box>
            <BackrsForm
              buttonLabel="Continue"
              fields={fields}
              methods={formMethods}
              onSubmit={async (data) => {
                try {
                  await goToNextQuestion(handleClearEmptyFields(data));
                } catch (error) {
                  handleErrorNavigation(error);
                }
              }}
            />

            {currentQuestion.subAction && (
              <Box sx={subActionContainerStyle}>
                <Typography variant="text-sm-regular">{currentQuestion.subAction.body}</Typography>
                <Typography
                  variant="text-sm-semibold"
                  component="a"
                  onClick={
                    currentQuestion.subAction.onClick === 'LOGIN'
                      ? () => navigate(APP_ROUTE.LOGIN)
                      : () => {
                          // Use handleSubmit to ensure proper validation,
                          formMethods.handleSubmit(async (data) => {
                            try {
                              await goToNextQuestion(handleClearEmptyFields(data));
                            } catch (error) {
                              handleErrorNavigation(error);
                            }
                          })();
                        }
                  }
                  sx={subActionLink}
                >
                  {currentQuestion.subAction.actionText}
                </Typography>
              </Box>
            )}
          </Box>

          {currentQuestionIndex === questionKeys.length - 1 && <TermsAndConditions />}
        </Box>
      )}
    </Box>
  );
};

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  margin: '0 auto',
  backgroundColor: backrs.colors['Colors/Base/White'],
  width: '100%',
  height: '100dvh',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
});

const contentContainerStyle =
  (isShowingAccountCreationLoading: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    alignSelf: 'center',
    justifySelf: 'center',
    margin: 'auto',
    backgroundColor: backrs.colors['Colors/Base/White'],
    maxHeight: '100dvh',
    overflowY: 'auto',
    paddingX: backrs.spacing['spacing-md'],
    paddingY: {
      xs: backrs.spacing['spacing-none'],
      sm: backrs.spacing['spacing-3xl'],
    },
    borderRadius: backrs.radius['radius-md'],
    display: isShowingAccountCreationLoading ? 'none' : 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
    gap: backrs.spacing['spacing-2xl'],
    width: '100%',
    boxSizing: 'border-box',
  });

const questionHeaderContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-4xl'],
  mb: backrs.spacing['spacing-4xl'],
});

const subActionContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  mt: backrs.spacing['spacing-3xl'],
  justifyContent: 'center',
  width: '100%',
});

const subActionLink: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const questionContainerStyle: SxProps<Theme> = {
  minWidth: { sm: '360px' },
  maxWidth: { sm: '360px' },
  width: { xs: '90vw', sm: 'unset' },
};

const loadingImageStyle: SxProps<Theme> = {
  width: { xs: '100%', sm: 'auto' },
  height: { xs: 'auto', sm: '100%' },
  objectFit: 'contain',
};

const loadingTextStyle: SxProps<Theme> = ({ typography, breakpoints }) => ({
  [breakpoints.down('sm')]: {
    ...typography['display-xs-semibold'],
  },
});

const questionTitleContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});
